import React, { useState, useEffect } from "react";
import {
    FormGroup,
    FormLabel,
    TextField,
    InputAdornment,
    Checkbox,
    Box,
    FormControlLabel,
    Tooltip
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface InputTextFieldProps {
    label?: string;
    value: string;
    startAdornment?: string;
    endAdornment?: string;
    inputType: string;
    error?: boolean;
    helperText?: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    onFocus?: () => void;
    disabled?: boolean;
    checkedValue?: boolean;
    onCheckboxChange?: (checked: boolean) => void;
    placeholder?: string;
    filedLable?: string;
    tooltipText?: string;
}

const MAX_LIMIT = 999999999;

function formatNumber(val: string) {
    const num = parseFloat(val.replace(/,/g, ""));
    if (!isNaN(num) && val !== "") {
        return num.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }
    return val;
}

const InputTextField: React.FC<InputTextFieldProps> = ({
    label,
    value,
    startAdornment,
    endAdornment,
    inputType,
    error = false,
    helperText = "",
    onChange,
    onBlur,
    onFocus,
    disabled = false,
    checkedValue,
    onCheckboxChange,
    placeholder = "",
    filedLable = "",
    tooltipText,
}) => {
    const [rawValue, setRawValue] = useState(value);
    const [displayValue, setDisplayValue] = useState(
        inputType === "number" ? formatNumber(value) : value
    );
    const [isFocused, setIsFocused] = useState(false);

    // Update displayValue when parent value changes (and not editing)
    useEffect(() => {
        if (!isFocused) {
            setDisplayValue(inputType === "number" ? formatNumber(value) : value);
            setRawValue(value);
        }
    }, [value, isFocused, inputType]);

    // Handle number input restrictions and formatting
    const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value.replace(/[^0-9.]/g, "");
        const decimalCount = (val.match(/\./g) || []).length;
        if (decimalCount > 1) {
            val = val.slice(0, val.lastIndexOf(".") + 1);
        }
        if (parseFloat(val) > MAX_LIMIT) {
            val = MAX_LIMIT.toString();
        }
        setRawValue(val);
        setDisplayValue(val);
        onChange(val);
    };

    const handleNumberBlur = () => {
        setIsFocused(false);
        if (rawValue && rawValue !== ".") {
            const num = parseFloat(rawValue);
            if (!isNaN(num)) {
                const rounded = Math.round(num * 100) / 100;
                const formatted = rounded.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                });
                setDisplayValue(formatted);
                setRawValue(rounded.toString());
                onChange(rounded.toFixed(2));
            }
        } else {
            setDisplayValue("");
            setRawValue("");
            onChange("");
        }
        if (onBlur) onBlur();
    };

    const handleNumberFocus = () => {
        setIsFocused(true);
        setDisplayValue(rawValue);
        if (onFocus) onFocus();
    };

    // For text input: allow any text
    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRawValue(e.target.value);
        setDisplayValue(e.target.value);
        onChange(e.target.value);
    };

    const handleTextBlur = () => {
        setIsFocused(false);
        if (onBlur) onBlur();
    };

    const handleTextFocus = () => {
        setIsFocused(true);
        if (onFocus) onFocus();
    };

    return (
        <FormGroup>
            {label !== undefined ? (
                <Box display="flex" alignItems="center">
                    {checkedValue !== undefined && onCheckboxChange ? (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={checkedValue}
                                    onChange={(e) => onCheckboxChange(e.target.checked)}
                                    color="secondary"
                                />
                            }
                            label={label}
                        />
                    ) : (
                            <FormLabel
                                className="input-field-label"
                                style={{ display: "flex", alignItems: "center" }}
                            >
                            {label}
                            {tooltipText && (
                                <Tooltip title={tooltipText} placement="top" arrow>
                                    <InfoOutlinedIcon
                                        fontSize="small"
                                        style={{ marginLeft: 4, cursor: "pointer", color: "#888" }}
                                    />
                                </Tooltip>
                            )}
                        </FormLabel>
                    )}
                </Box>
            ) : null}

            <TextField
                variant="outlined"
                color="secondary"
                value={displayValue}
                InputProps={{
                    startAdornment: startAdornment ? (
                        <InputAdornment position="start">{startAdornment}</InputAdornment>
                    ) : undefined,
                    endAdornment: endAdornment ? (
                        <InputAdornment position="end">{endAdornment}</InputAdornment>
                    ) : undefined,
                }}
                onChange={
                    inputType === "number" ? handleNumberChange : handleTextChange
                }
                onBlur={
                    inputType === "number" ? handleNumberBlur : handleTextBlur
                }
                onFocus={
                    inputType === "number" ? handleNumberFocus : handleTextFocus
                }
                type={inputType === "number" ? "text" : inputType}
                inputProps={{
                    inputMode: inputType === "number" ? "decimal" : undefined,
                }}
                fullWidth
                error={error}
                helperText={helperText}
                disabled={disabled}
                placeholder={placeholder}
                label={filedLable}
            />
        </FormGroup>
    );
};

export default InputTextField;
